/**
 * What Edit the setup can build (step 9.16 of
 * docs/plans/onboarding-harness/phase-09.md): the difference between the
 * workspace as it stood when the edit session was opened (its seed, version 1)
 * and the draft as it stands now, as the `SetupEditChange`s Build again writes, and
 * every other difference as an item Edit cannot build yet, with why.
 *
 * The seed is `draftFromWorkspace(…, 'edit')`: a part's path never changes once
 * minted, so a part of the draft is the built part of the same path, and
 * `built` names its id. A part the seed did not hold is new.
 *
 *   a new field on a built record      change.add
 *   a built field renamed              change.rename
 *   a built field taken out            change.archive (hidden, its values kept)
 *   a built field's must-fill or a     change.patch
 *   select's new choice
 *   a new record or list               add, with its fields and a list's names
 *   a built record left out            leave_out (archived with its entries)
 *   a built record renamed             rename_record
 *   a new card, a built card taken off add_card, remove_card (a changed card is both)
 *   a money rule, Accounts rows, a     money (V2.2)
 *   charge plan, approvals, tasks
 *
 * How a record counts money, which of its entries wait for approval, and the
 * tasks it raises are built too (step V2.2 of docs/plans/ledger.md), as one
 * `money` change for each record whose parts differ: a new or changed money
 * rule (a changed one counts new saves only), the rule taken off, new rows of
 * the Accounts list with their accounts, the Accounts mark on a new list, a
 * list's charge plan, an approval chain, and task rules added. A new record or
 * list that counts money is an add and its money change, so a pick of it
 * builds beside it.
 *
 * Everything else that differs is not yet: a built field's type or a pick's
 * list (`type_change`), its recipe (`formula_change`), its lines
 * (`lines_change`), a role (`role_change`, roles are changed on Team), the
 * money the workspace counts in (`currency_change`), and a part the seed kept
 * out because a draft cannot say it, or a new field of a kind Edit cannot add
 * (`kept_part`). A stock items mark, the Accounts mark moved on a built list,
 * and a row of the Accounts list whose type the draft does not say are not
 * yet either (`money_change`).
 * What the draft changes that a workspace does not hold (a
 * field's help line, a record's headline) is not written and is not counted.
 *
 * Pure: the studio counts "Build again · N" with it, and the server builds it.
 */
import type { RecordBlankCounts } from './api';
import type { SetupDraftField } from './compose-author';
import type { BuiltKept } from './draft-from-workspace';
import { isLedgerAccountType } from './ledger/patterns';
import {
  SETUP_CHANGE_LIMITS,
  SETUP_FIELD_TYPES,
  type SetupAccountRowAdd,
  type SetupChangeMade,
  type SetupEditChange,
  type SetupFieldAdd,
  type SetupFieldPatch,
  type SetupFieldRename,
  type SetupFieldType,
  type SetupMoneyChange,
  type SetupRecordRef,
} from './setup-edit';
import type { StudioHeldDirections } from './setup-draft/commit';
import type { StudioDraft, StudioRecord, StudioRole, StudioTile } from './setup-draft/draft';
import { studioPath } from './setup-draft/paths';

export const EDIT_NOT_YET_WHY = ['type_change', 'formula_change', 'lines_change', 'role_change', 'currency_change', 'kept_part', 'money_change'] as const;
export type EditNotYet = (typeof EDIT_NOT_YET_WHY)[number];

/** One difference Build again leaves as it is: the part path, why, and the part's name. */
export type EditNotYetItem = { readonly key: string; readonly why: EditNotYet; readonly name: string };

/**
 * An edit session's base (`setup_session.base`): the seed draft (version 1),
 * the id each of its part paths stands for, what the seed kept out, and each
 * built record's `field_set_ver` when it was read.
 */
export type EditBase = {
  readonly draft: StudioDraft;
  readonly built: Readonly<Record<string, string>>;
  readonly kept: readonly BuiltKept[];
  readonly fieldSetVers: Readonly<Record<string, number>>;
  /** How many entries (rows, on a list) each built record held when it was read, by record id: what OS and the Build again sheet say is kept. */
  readonly entries?: Readonly<Record<string, number>>;
};

export type EditChanges = { changes: SetupEditChange[]; notYet: EditNotYetItem[] };

/**
 * What Build again made (`POST /v1/setup/sessions/:id/build` on an edit
 * session, 9.16), kept on the session as its build result: each record it
 * touched, the first record added or changed (for "Open <record>"), and what
 * it left as it is.
 */
export type SetupEditBuildResult = {
  readonly kind: 'edit';
  readonly tenantId: string;
  readonly builtAt: string;
  /** The version built. */
  readonly version: number;
  readonly made: readonly SetupChangeMade[];
  readonly openRecordId: string | null;
  /** How many changes were built: `editChangesOf(…).changes.length`. */
  readonly changes: number;
  readonly notYet: readonly EditNotYetItem[];
};

export type SetupEditBuildResponse = { readonly status: 'built'; readonly edit: SetupEditBuildResult };

/** The catalogue key of an item's sentence (`setup.studio.edit.not_yet_<why>`). */
type EditNotYetWord = 'type' | 'formula' | 'lines' | 'role' | 'currency' | 'kept' | 'money';
export const editNotYetKey = (why: EditNotYet): `setup.studio.edit.not_yet_${EditNotYetWord}` =>
  `setup.studio.edit.not_yet_${why.replace('_change', '').replace('_part', '') as EditNotYetWord}`;

/* ---------------------------------------------------------------- helpers */

/** The same value, whatever order its keys were written in. */
const canonical = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value !== null && typeof value === 'object') {
    const held = value as Record<string, unknown>;
    return `{${Object.keys(held)
      .filter((key) => held[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(held[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
};
const same = (a: unknown, b: unknown): boolean => canonical(a) === canonical(b);

const ADDABLE: ReadonlySet<string> = new Set(SETUP_FIELD_TYPES);

const COUNTS: Record<StudioRecord['direction'], RecordBlankCounts> = { in: 'money_in', out: 'money_out', both: 'money_both', count: 'count', none: 'none' };

const recordPath = (record: string): string => studioPath({ kind: 'record', record });
const fieldPath = (record: string, field: string): string => studioPath({ kind: 'field', record, field });
const cardPath = (record: string, card: string): string => studioPath({ kind: 'card', record, card });
const rolePath = (key: string): string => studioPath({ kind: 'role', key });

/** A record's task rules, none when it holds an empty list. */
const tasksOf = (record: StudioRecord): StudioRecord['tasks'] => (record.tasks === undefined || record.tasks.length === 0 ? undefined : record.tasks);

/** The words of a list's first names: its headline's value on each seed row, at most `SETUP_CHANGE_LIMITS.rows`, once each. */
const rowsOf = (record: StudioRecord): string[] => {
  const names: string[] = [];
  for (const row of record.seedRows ?? []) {
    const value = row.values.find((one) => one.fieldKey === record.headlineFieldKey)?.value;
    const word = value === undefined ? '' : String(value).trim();
    if (word !== '' && !names.some((one) => one.toLowerCase() === word.toLowerCase())) names.push(word);
  }
  return names.slice(0, SETUP_CHANGE_LIMITS.rows);
};

type SeedRow = NonNullable<StudioRecord['seedRows']>[number];

/** A row's name: its headline's value, trimmed. */
const rowNameOf = (record: StudioRecord, row: SeedRow): string => {
  const value = row.values.find((one) => one.fieldKey === record.headlineFieldKey)?.value;
  return value === undefined ? '' : String(value).trim();
};

/**
 * The type a row of the Accounts list names: its Type value as a type, or the
 * Type option it names by key or by words, read as a type. Null when it says
 * none of them.
 */
const rowTypeOf = (record: StudioRecord, row: SeedRow): SetupAccountRowAdd['type'] | null => {
  const typeKey = record.accounts?.typeFieldKey;
  const said = row.values.find((one) => one.fieldKey === typeKey)?.value;
  if (said === undefined) return null;
  const word = String(said).trim();
  if (isLedgerAccountType(word)) return word;
  const option = record.fields.find((field) => field.key === typeKey)?.options?.find((one) => one.key === word || one.label.trim().toLowerCase() === word.toLowerCase());
  return option !== undefined && isLedgerAccountType(option.key) ? option.key : null;
};

/* ------------------------------------------------------------------- read */

/**
 * The draft's difference from its base, as changes Build again writes and
 * items it leaves as they are. The changes come in the draft's order: renames
 * and field changes of built records, new records and lists, records left out,
 * then links, then cards.
 */
export const editChangesOf = (base: EditBase, draft: StudioDraft): EditChanges => {
  const changes: SetupEditChange[] = [];
  const notYet: EditNotYetItem[] = [];
  const keptPaths = new Set(base.kept.map((one) => one.key));
  const note = (key: string, why: EditNotYet, name: string): void => {
    if (!notYet.some((one) => one.key === key && one.why === why)) notYet.push({ key, why, name });
  };

  const baseRecords = new Map(base.draft.records.map((record) => [record.key, record]));
  const idOf = (path: string): string | undefined => base.built[path];
  /** A record the set reaches: built (its id) or new in this draft (its key). */
  const refOf = (recordKey: string): SetupRecordRef => {
    const id = baseRecords.has(recordKey) ? idOf(recordPath(recordKey)) : undefined;
    return id === undefined ? { recordKey } : { recordId: id };
  };

  /** A new field as an add, or null with why when Edit cannot add its kind. */
  const addOf = (record: StudioRecord, field: SetupDraftField): SetupFieldAdd | null => {
    if (!ADDABLE.has(field.type)) {
      note(fieldPath(record.key, field.key), field.type === 'formula' ? 'formula_change' : field.type === 'lines' ? 'lines_change' : 'kept_part', field.label);
      return null;
    }
    const target = field.type === 'reference' && field.targetRecordKey !== undefined ? refOf(field.targetRecordKey) : null;
    return {
      key: field.key,
      label: field.label,
      type: field.type as SetupFieldType,
      ...(field.required === true ? { required: true } : {}),
      ...(field.type === 'select' && field.options !== undefined ? { options: field.options.map((option) => option.label) } : {}),
      // The keys go too, so a rule that reads a choice by its key (a discount's `cycles`) reads the field Build again adds.
      ...(field.type === 'select' && field.options !== undefined && field.options.length > 0 && field.options.every((option) => typeof option.key === 'string' && option.key !== '')
        ? { optionKeys: field.options.map((option) => option.key) }
        : {}),
      ...(target === null ? {} : 'recordId' in target ? { targetRecordId: target.recordId } : { targetRecordKey: target.recordKey }),
      ...(field.type === 'reference' && field.accounts !== undefined ? { accounts: structuredClone(field.accounts) } : {}),
    };
  };

  /**
   * A record's money parts that differ from what it was (nothing, for a new
   * one), as one money change, or none. What Build again cannot write is
   * noted `money_change`.
   */
  const moneyChanges: SetupEditChange[] = [];
  const moneyOf = (record: StudioRecord, was: StudioRecord | undefined, ref: SetupRecordRef): void => {
    const change: { -readonly [K in keyof SetupMoneyChange]: SetupMoneyChange[K] } = { kind: 'money', record: ref, recordKey: record.key };
    if (record.kind === 'record' && !same(record.posting, was?.posting)) {
      change.posting = record.posting === undefined ? null : structuredClone(record.posting);
      if (was?.posting !== undefined) change.ruleHeld = true;
    }
    const markHeld = same(record.accounts, was?.accounts);
    if (!markHeld) {
      if (was === undefined && record.kind === 'directory') change.accounts = structuredClone(record.accounts!);
      else note(recordPath(record.key), 'money_change', record.name);
    }
    if (record.accounts !== undefined && (was === undefined || markHeld)) {
      const held = new Set((was?.seedRows ?? []).map((row) => rowNameOf(was!, row).toLowerCase()));
      const rows: SetupAccountRowAdd[] = [];
      for (const row of record.seedRows ?? []) {
        const name = rowNameOf(record, row);
        if (name === '' || held.has(name.toLowerCase()) || rows.some((one) => one.name.toLowerCase() === name.toLowerCase())) continue;
        const type = rowTypeOf(record, row);
        if (type === null) {
          note(recordPath(record.key), 'money_change', name);
          continue;
        }
        rows.push({ name, type, ...(row.account === undefined ? {} : { account: structuredClone(row.account) }) });
      }
      if (rows.length > 0) change.accountRows = rows;
    }
    if (!same(record.stockItems, was?.stockItems)) note(recordPath(record.key), 'money_change', record.name);
    if (!same(record.chargePlan, was?.chargePlan)) {
      if (record.kind === 'directory' && record.chargePlan !== undefined) change.chargePlan = structuredClone(record.chargePlan);
      else note(recordPath(record.key), 'money_change', record.name);
    }
    if (record.kind === 'record' && !same(record.approvalChain, was?.approvalChain)) {
      change.approvalChain = record.approvalChain === undefined ? null : structuredClone(record.approvalChain);
    }
    // Task rules are added after the ones the record holds; one already held is not added again.
    const wasTasks = tasksOf(was ?? { ...record, tasks: [] }) ?? [];
    const added = record.kind === 'record' ? (tasksOf(record) ?? []).filter((rule) => !wasTasks.some((one) => same(one, rule))) : [];
    if (added.length > 0) change.tasks = structuredClone(added);
    if (Object.keys(change).length > 3) moneyChanges.push(change);
  };

  const cardChanges: SetupEditChange[] = [];
  const newCard = (record: StudioRecord, tile: StudioTile): void => {
    cardChanges.push({ kind: 'add_card', record: refOf(record.key), recordKey: record.key, tile: structuredClone(tile) });
  };

  /* the workspace */
  if (draft.meta.currency !== base.draft.meta.currency) note('meta', 'currency_change', draft.meta.name);

  /* records and lists the seed held */
  const draftRecords = new Map(draft.records.map((record) => [record.key, record]));
  const leftOut: SetupEditChange[] = [];
  for (const was of base.draft.records) {
    const recordId = idOf(recordPath(was.key));
    if (recordId === undefined) continue;
    const now = draftRecords.get(was.key);
    if (now === undefined) {
      leftOut.push({ kind: 'leave_out', recordId });
      continue;
    }
    if (now.name.trim() !== was.name.trim()) changes.push({ kind: 'rename_record', recordId, name: now.name.trim() });

    const add: SetupFieldAdd[] = [];
    const archive: string[] = [];
    const rename: SetupFieldRename[] = [];
    const patch: SetupFieldPatch[] = [];
    const nowFields = new Map(now.fields.map((field) => [field.key, field]));
    for (const field of was.fields) {
      const fieldId = idOf(fieldPath(was.key, field.key));
      if (fieldId === undefined) continue;
      const held = nowFields.get(field.key);
      if (held === undefined) {
        archive.push(fieldId);
        continue;
      }
      const at = fieldPath(was.key, field.key);
      if (held.type !== field.type || (held.targetRecordKey ?? null) !== (field.targetRecordKey ?? null)) {
        note(at, 'type_change', held.label);
        continue;
      }
      if (held.type === 'formula' && !same(held.formula, field.formula)) note(at, 'formula_change', held.label);
      if (held.type === 'lines' && !same(held.lines, field.lines)) note(at, 'lines_change', held.label);
      if (held.label.trim() !== field.label.trim()) rename.push({ fieldId, label: held.label.trim() });
      const one: { fieldId: string; required?: boolean; addOptions?: string[] } = { fieldId };
      if ((held.required === true) !== (field.required === true)) one.required = held.required === true;
      if (held.type === 'select') {
        const before = field.options ?? [];
        const after = held.options ?? [];
        const kept = before.every((option) => after.some((other) => other.key === option.key && other.label.trim() === option.label.trim()));
        if (!kept) note(at, 'type_change', held.label);
        else {
          const added = after.filter((option) => !before.some((other) => other.key === option.key)).map((option) => option.label.trim());
          if (added.length > 0) one.addOptions = added;
        }
      }
      if (one.required !== undefined || one.addOptions !== undefined) patch.push(one);
    }
    for (const field of now.fields) {
      if (was.fields.some((one) => one.key === field.key)) continue;
      if (keptPaths.has(fieldPath(was.key, field.key))) {
        note(fieldPath(was.key, field.key), 'kept_part', field.label);
        continue;
      }
      const made = addOf(now, field);
      if (made !== null) add.push(made);
    }
    /* how it counts money, its approvals and its tasks (V2.2) */
    moneyOf(now, was, { recordId });

    if (add.length + archive.length + rename.length + patch.length > 0) {
      changes.push({
        kind: 'change',
        recordId,
        fieldSetVer: base.fieldSetVers[recordId] ?? 0,
        ...(add.length === 0 ? {} : { add }),
        ...(archive.length === 0 ? {} : { archive }),
        ...(rename.length === 0 ? {} : { rename }),
        ...(patch.length === 0 ? {} : { patch }),
      });
    }

    /* its cards on top */
    const wasTiles = new Map((was.tiles ?? []).map((tile) => [tile.key, tile]));
    for (const tile of was.tiles ?? []) {
      const cardId = idOf(cardPath(was.key, tile.key));
      if (cardId === undefined) continue;
      const held = (now.tiles ?? []).find((one) => one.key === tile.key);
      if (held !== undefined && same(held, tile)) continue;
      cardChanges.push({ kind: 'remove_card', recordId, cardId });
      if (held !== undefined) newCard(now, held);
    }
    for (const tile of now.tiles ?? []) {
      if (wasTiles.has(tile.key)) continue;
      if (keptPaths.has(cardPath(was.key, tile.key))) note(cardPath(was.key, tile.key), 'kept_part', tile.label);
      else newCard(now, tile);
    }
  }

  /* records and lists the seed did not hold */
  for (const record of draft.records) {
    if (baseRecords.has(record.key)) continue;
    if (keptPaths.has(recordPath(record.key))) {
      note(recordPath(record.key), 'kept_part', record.name);
      continue;
    }
    const fields = record.fields.flatMap((field) => {
      const made = addOf(record, field);
      return made === null ? [] : [made];
    });
    // The Accounts list's rows come with their accounts in its money change, so none stands without one (L15).
    const rows = record.kind === 'directory' && record.accounts === undefined ? rowsOf(record) : [];
    changes.push({
      kind: 'add',
      key: record.key,
      recordKind: record.kind,
      name: record.name.trim(),
      ...(record.kind === 'record' ? { counts: COUNTS[record.direction] } : {}),
      fields,
      ...(rows.length === 0 ? {} : { rows }),
    });
    for (const tile of record.tiles ?? []) newCard(record, tile);
    moneyOf(record, undefined, { recordKey: record.key });
  }
  changes.push(...leftOut);
  changes.push(...moneyChanges);

  changes.push(...cardChanges);

  /* roles: changed on Team */
  const roleOf = (roles: readonly StudioRole[], key: string): StudioRole | undefined => roles.find((role) => role.key === key);
  for (const key of new Set([...base.draft.roles.map((role) => role.key), ...draft.roles.map((role) => role.key)])) {
    const was = roleOf(base.draft.roles, key);
    const now = roleOf(draft.roles, key);
    if (was !== undefined && now !== undefined && same(was, now)) continue;
    note(rolePath(key), 'role_change', (now ?? was)!.name);
  }

  return { changes, notYet };
};

/**
 * The money direction of every built record that holds at least one entry,
 * by its key, as the seed had it: an edit session's commit refuses a change
 * to one (`studioCommit`'s `heldDirections`), and so does Build again.
 * `entries` gives the counts by record id; the base's own, read when Edit was
 * opened, by default. A built record with no entries may still change.
 */
export const editHeldDirections = (base: EditBase, entries: Readonly<Record<string, number>> = base.entries ?? {}): StudioHeldDirections =>
  Object.fromEntries(
    base.draft.records.flatMap((record) => {
      const id = base.built[recordPath(record.key)];
      if (record.kind !== 'record' || id === undefined || (entries[id] ?? 0) < 1) return [];
      return [[record.key, { direction: record.direction, name: record.name }] as const];
    }),
  );

/** Reads a stored `setup_session.base`, or null when it is not one. */
export const readEditBase = (raw: unknown): EditBase | null => {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;
  const draft = held.draft as StudioDraft | undefined;
  if (draft === undefined || draft === null || typeof draft !== 'object' || !Array.isArray(draft.records)) return null;
  const built = held.built !== null && typeof held.built === 'object' ? (held.built as Record<string, string>) : {};
  const kept = Array.isArray(held.kept) ? (held.kept as BuiltKept[]) : [];
  const fieldSetVers = held.fieldSetVers !== null && typeof held.fieldSetVers === 'object' ? (held.fieldSetVers as Record<string, number>) : {};
  const entries = held.entries !== null && typeof held.entries === 'object' ? (held.entries as Record<string, number>) : undefined;
  return { draft, built, kept, fieldSetVers, ...(entries === undefined ? {} : { entries }) };
};
